import { useI18n } from '../../../i18n';
import type { PromotionStatus } from '@conai/shared-types';
import { EditIcon, TrashIcon } from '../../../components/icons';
import { Pagination } from '../../../components/shared';

export interface PromotionTableItem {
  id: string;
  code: string;
  name: string;
  discount: string;
  discountType: 'PERCENTAGE' | 'FIXED_AMOUNT';
  period: string;
  status: PromotionStatus;
}

interface PromotionTableProps {
  data: PromotionTableItem[];
  isLoading: boolean;
  onView: (item: PromotionTableItem) => void;
  onEdit: (item: PromotionTableItem) => void;
  onDelete: (item: PromotionTableItem) => void;
  currentPage: number;
  totalPages: number;
  totalItems: number;
  pageSize: number;
  onPageChange: (page: number) => void;
}

function StatusBadge({ status }: { status: PromotionStatus }) {
  const { t } = useI18n();
  const config: Record<string, { label: string; className: string }> = {
    DRAFT: { label: t('draft'), className: 'bg-slate-100 text-slate-700' },
    ACTIVE: { label: t('active'), className: 'bg-green-100 text-green-700' },
    PAUSED: { label: t('inactiveStatus') || 'Inactive', className: 'bg-red-100 text-red-700' },
    INACTIVE: { label: t('inactiveStatus') || 'Inactive', className: 'bg-red-100 text-red-700' },
    EXPIRED: { label: t('expired'), className: 'bg-amber-100 text-amber-700' },
    CANCELLED: { label: t('inactiveStatus') || 'Inactive', className: 'bg-red-100 text-red-700' },
  };
  const { label, className } = config[status as string] || config.DRAFT;

  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${className}`}
    >
      {label}
    </span>
  );
}

/**
 * Bug #147: cột Discount trước đây chỉ có con số ("15%" / "₴50") và phân biệt
 * kiểu giảm giá bằng… độ đậm + màu chữ — một quy ước không ai đọc ra. Người dùng
 * nhìn thấy chữ "Percentage" (ở thẻ thống kê, ở modal chi tiết) mà không biết nó
 * nói về cái gì.
 *
 * Nay kiểu giảm giá là một BADGE nằm ngay cạnh giá trị thật ("Percentage · 15%"),
 * dùng đúng khuôn badge của `StatusBadge` ở trên cho nhất quán với toàn app, và
 * `title` nói rõ đây là "Discount type" khi rê chuột.
 *
 * Màu badge khớp với hai thẻ đếm ở `PromotionStatsRow` (fuchsia = Percentage,
 * amber = Fixed amount) để người dùng nối được thẻ đếm với dòng trong bảng.
 */
function DiscountTypeBadge({ type }: { type: PromotionTableItem['discountType'] }) {
  const { t } = useI18n();
  const isPercentage = type === 'PERCENTAGE';

  return (
    <span
      title={t('discountType')}
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
        isPercentage ? 'bg-fuchsia-100 text-fuchsia-700' : 'bg-amber-100 text-amber-700'
      }`}
    >
      {isPercentage ? t('percentage') : t('fixedAmount')}
    </span>
  );
}

export function PromotionTable({
  data, isLoading, onView, onEdit, onDelete,
  currentPage, totalPages, totalItems, pageSize, onPageChange,
}: PromotionTableProps) {
  const { t } = useI18n();
  if (isLoading) {
    return (
      <div className="rounded-xl border border-slate-100 bg-white p-8 shadow-sm">
        <div className="flex items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border border-slate-100 bg-white shadow-sm">
      <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-slate-200">
        <thead className="bg-slate-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-600">
              {t('code')}
            </th>
            <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-600">
              {t('name')}
            </th>
            <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-600">
              {t('discount')}
            </th>
            <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-600">
              {t('period')}
            </th>
            <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-600">
              {t('status')}
            </th>
            <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-600">
              {t('action')}
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-200">
          {data.map((item) => (
            <tr key={item.id} className="transition-colors hover:bg-slate-50 cursor-pointer" onClick={() => onView(item)}>
              <td className="px-6 py-4">
                <span
                  className="text-sm font-medium uppercase leading-[29px]"
                  style={{
                    color: 'var(--text-black-212121, #212121)',
                    fontFamily: 'Inter, sans-serif'
                  }}
                >
                  {item.code}
                </span>
              </td>
              <td className="px-6 py-4 text-sm text-[#666]">{item.name}</td>
              <td className="px-6 py-4">
                {/* Bug #147: giá trị thật + nhãn kiểu giảm giá đứng cạnh nhau. */}
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-semibold" style={{ color: '#212121' }}>
                    {item.discount}
                  </span>
                  <DiscountTypeBadge type={item.discountType} />
                </div>
              </td>
              <td className="px-6 py-4 text-sm text-slate-500">{item.period}</td>
              <td className="px-6 py-4">
                <StatusBadge status={item.status} />
              </td>
              <td className="px-6 py-4">
                <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() => onEdit(item)}
                    className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-primary-600"
                    title={t('edit') || 'Edit'}
                  >
                    <EditIcon size={16} />
                  </button>
                  <button
                    onClick={() => onDelete(item)}
                    className="rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-600"
                    title={t('delete') || 'Delete'}
                  >
                    <TrashIcon size={16} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
          {data.length === 0 && (
            <tr>
              <td colSpan={6} className="px-6 py-12 text-center text-sm text-slate-500">
                {t('noPromotionsFound')}
              </td>
            </tr>
          )}
        </tbody>
      </table>
      </div>
      {totalItems > 0 && (
        <div className="border-t border-slate-100">
          <Pagination
            variant="numbered"
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            pageSize={pageSize}
            onPageChange={onPageChange}
            itemLabel="promotions"
          />
        </div>
      )}
    </div>
  );
}
